'use client'

import { useEffect, useState } from 'react'
import { platformFetch } from '@/lib/platform/client-headers'

export interface TrendPoint {
  checkedAt: string
  overallScore: number
  grade: string
  sentimentBreakdown?: {
    positive?: number
    neutral?: number
    negative?: number
    cautionary?: number
  }
  sovData?: {
    sovPercent?: number
    rank?: number
  }
}

/**
 * Shared loader for `/api/platform/trend-data`.
 *
 * Extracted so the hero sparkline and the full History chart read the same
 * endpoint through one code path — two independent fetches of the same series
 * is how they drift.
 *
 * Uses `platformFetch`: the route is authenticated, and a bare fetch 401s for
 * any token-only user (no session cookie). This hook previously used a plain
 * `fetch`, which is why the sparkline reported "no history yet" to users who
 * had plenty.
 *
 * `error` is distinct from an empty `data` on purpose. A failed request tells
 * us nothing about how many audits the customer has run, so callers must not
 * render "you have no history" off the back of one.
 *
 * Passing `external` short-circuits the request entirely.
 */
export function useTrendHistory(
  domain: string | null | undefined,
  external?: TrendPoint[],
  limit = 20
) {
  const [data, setData] = useState<TrendPoint[]>(external ?? [])
  const [loading, setLoading] = useState(!external && !!domain)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (external) {
      setData(external)
      setLoading(false)
      setError(false)
      return
    }
    if (!domain) {
      setData([])
      setLoading(false)
      setError(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(false)
    platformFetch(
      `/api/platform/trend-data?domain=${encodeURIComponent(domain)}&limit=${limit}`
    )
      .then(async res => {
        if (!res.ok) throw new Error(`trend-data ${res.status}`)
        const json = await res.json()
        if (!cancelled) setData(Array.isArray(json.history) ? json.history : [])
      })
      .catch(() => {
        if (!cancelled) {
          setData([])
          setError(true)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [domain, external, limit])

  return { data, loading, error }
}
